import { format } from 'date-fns';

const DONE_STATUSES = new Set(['completed', 'cancelled', 'no_show']);

/** Minimal appointment shape needed to pick “next up”. */
export interface NextUpCandidate {
  id: string;
  patientName?: string | null;
  scheduledAt: string;
  status: string;
  providerId?: string | null;
  doctorId?: string | null;
}

export interface HeaderNextUpItem {
  id: string;
  timeLabel: string;
  patientFirstName: string;
  scheduledAt: string;
}

function patientFirstName(fullName: string | null | undefined): string {
  const trimmed = fullName?.trim();
  if (!trimmed) return 'Patient';
  return trimmed.split(/\s+/)[0] ?? 'Patient';
}

function timeLabel(iso: string): string {
  try {
    return format(new Date(iso), 'HH:mm');
  } catch {
    return '--:--';
  }
}

function matchesProvider(appt: NextUpCandidate, providerId: string): boolean {
  return appt.providerId === providerId || appt.doctorId === providerId;
}

/**
 * Picks the next actionable appointment for the header cue.
 * Prefers upcoming (from ~15 minutes ago onward); falls back to earliest active.
 * When `providerId` is set (e.g. doctor session), filters to that provider.
 */
export function pickNextUp(
  appointments: NextUpCandidate[],
  options?: { providerId?: string | null; nowMs?: number },
): HeaderNextUpItem | null {
  const nowMs = options?.nowMs ?? Date.now();
  const providerId = options?.providerId?.trim() || null;

  let active = appointments.filter((a) => !DONE_STATUSES.has(a.status));
  if (providerId) {
    active = active.filter((a) => matchesProvider(a, providerId));
  }
  if (active.length === 0) return null;

  const sorted = [...active].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  const windowStart = nowMs - 15 * 60_000;
  const upcoming =
    sorted.find((a) => new Date(a.scheduledAt).getTime() >= windowStart) ?? sorted[0] ?? null;
  if (!upcoming) return null;

  return {
    id: upcoming.id,
    timeLabel: timeLabel(upcoming.scheduledAt),
    patientFirstName: patientFirstName(upcoming.patientName),
    scheduledAt: upcoming.scheduledAt,
  };
}
